import * as Sentry from '@sentry/react'
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  PackageIcon,
  SearchIcon,
  FilterIcon,
  EyeIcon,
  ShoppingCartIcon,
  CpuIcon,
  DatabaseIcon,
  HardDriveIcon,
  XIcon,
  BuildingIcon,
  CheckCircleIcon,
  PlayIcon,
  GlobeIcon,
  EyeOffIcon,
  UserIcon,
  LockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ServerIcon,
  AlertTriangleIcon,
} from 'lucide-react'
import { useQueryState, debounce } from 'nuqs'
import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { ResponsiveActionRow, ResponsivePageHeader } from '@nebula/shared/components'
import { AlertModal } from '@nebula/shared/components/modals'
import { apiClient } from '@nebula/shared/services/api'
import { publicIpApi } from '@nebula/shared/services/publicIpApi'
import type { PublicIPAllocated } from '@nebula/shared/services/publicIpApi'
import { vpcApi } from '@nebula/shared/services/vpcApi'
import type { VPCListItem } from '@nebula/shared/services/vpcApi'
import { useAuthStore } from '@nebula/shared/store'
import { DEFAULT_VM_USER, getApiErrorText, getErrorMessage } from '@nebula/shared/utils'
import { formatRecurringPrice } from '@nebula/shared/utils/format'

import CreateVPCModal from '../components/CreateVPCModal'
import InfraSetupBanner from '../components/InfraSetupBanner'
import { useInfraStatus } from '../hooks/useInfraStatus'
import { montarAlertaDeCriacao, TITULO_PROVISIONAMENTO } from './criacaoDeVmAlerta'
import type { RespostaDeCriacaoDeVm } from './criacaoDeVmAlerta'
import { decidirAcaoDeErroDeCriacao } from './criacaoDeVmErro'
import {
  camposDeRedePublica,
  descricaoDeCobranca,
  modoValidoPara,
  motivoParaNaoCriar,
  opcoesDeEnderecoPublico,
  WAN_MODE_POOL,
} from './criacaoDeVmRedePublica'
import type { WanMode } from './criacaoDeVmRedePublica'

// Local interfaces for end customer products view
interface Product {
  id: string
  name: string
  description?: string
  product_type: string
  specifications?: Record<string, any>
  pricing_model?: 'fixed' | 'per_resource'
  base_price: number
  original_provider_price?: number
  currency: string
  billing_cycle: string
  price_per_cpu?: number
  price_per_memory_gb?: number
  price_per_storage_gb?: number
  setup_fee?: number
  original_setup_fee?: number
  min_estimated_price?: number
  max_estimated_price?: number
  is_active: boolean
  is_public: boolean
  requires_cpu?: number
  requires_memory?: number
  requires_storage?: number
  min_cpu?: number
  max_cpu?: number
  min_memory?: number
  max_memory?: number
  min_storage?: number
  max_storage?: number
  min_quantity?: number
  max_quantity?: number
  tags?: string[]
  features?: string[]
  cluster_id?: string
  organization_id: string
  organization_name?: string
  reseller_id: string
  reseller_name: string
  markup_applied?: boolean
  markup_details?: any
  final_price?: number
  available_templates?: Array<{
    id: string
    name: string
    category: string
    os_type?: string
    os_variant?: string
    is_default: boolean
  }>
  created_at: string
  updated_at: string
}

interface ProductListFilters {
  skip?: number
  limit?: number
  search?: string
  product_type?: string
  reseller_id?: string
}

interface ProductListResponse {
  products: Product[]
  total: number
  has_more: boolean
}

interface ResolvedInstanceCluster {
  cluster_id: string
  /**
   * O cluster de destino fornece DHCP na WAN (issue #1061). É por ESTA resposta que a
   * opção "endereço por DHCP" aparece em "Internet Direto" — ausente (backend antigo)
   * ou falsa, ela não aparece.
   */
  wan_dhcp_available?: boolean
}

interface ResellerInfo {
  reseller?: {
    id: string
    name: string
    description?: string
    is_active: boolean
  }
  customer?: {
    id: string
    name: string
    parent_id?: string
  }
  is_captive?: boolean
  message?: string
}

interface ResellerConfig {
  payment_methods_enabled: {
    credit_card: boolean
    credit_account: boolean
    pix: boolean
    boleto: boolean
  }
}

interface AssociationsResponse {
  associations: Array<{
    id: string
    reseller_id: string
    reseller_name?: string
    status: string
    [key: string]: unknown
  }>
}

interface PaymentMethod {
  id: string
  card_brand?: string
  card_last_four?: string
  is_active: boolean
  is_default?: boolean
  [key: string]: unknown
}

interface PaymentMethodsResponse {
  payment_methods: PaymentMethod[]
}

interface CreditBalanceData {
  available_credit: number
  currency?: string
  has_credit_account: boolean
  balance?: number
  [key: string]: unknown
}

const MarketplacePage = () => {
  const infraStatus = useInfraStatus()
  const [showCreateVPCModal, setShowCreateVPCModal] = useState(false)
  const [filters, setFilters] = useState<ProductListFilters>({
    limit: 20,
    skip: 0,
  })
  const [searchTerm, setSearchTerm] = useQueryState('q', {
    defaultValue: '',
    history: 'replace',
    clearOnDefault: true,
    limitUrlUpdates: debounce(300),
  })
  const trimmedSearch = searchTerm.trim()
  const [showFilters, setShowFilters] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showInstanceModal, setShowInstanceModal] = useState(false)
  const [showIPv4Modal, setShowIPv4Modal] = useState(false)
  // Clusters candidatos devolvidos pelo 409 de ambiguidade (issue #691): o
  // provedor tem IPv4 livre em mais de um cluster e o backend nao chuta qual
  // usar, porque a VM so aceita anexar IP do proprio cluster. Sem este seletor
  // o 409 seria um beco sem saida — o cliente leria o motivo e nao teria acao.
  const [ipv4ClusterOptions, setIpv4ClusterOptions] = useState<Array<{ id: string; name: string }>>(
    [],
  )
  const [selectedIpv4ClusterId, setSelectedIpv4ClusterId] = useState<string | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [selectedIPv4Product, setSelectedIPv4Product] = useState<Product | null>(null)
  const [isIPv4PurchaseForInstance, setIsIPv4PurchaseForInstance] = useState(false)
  const [instanceQuantity, setInstanceQuantity] = useState(1)
  const [instanceNames, setInstanceNames] = useState<string[]>([''])
  const [paymentOption, setPaymentOption] = useState<'credit_card' | 'credit_account'>(
    'credit_card',
  )
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null)

  // Network mode selection
  const [networkMode, setNetworkMode] = useState<'vpc' | 'public_bridge'>('vpc')
  const [selectedPublicIpId, setSelectedPublicIpId] = useState<string | null>(null)
  // De onde vem o endereço em "Internet Direto" (issue #1061): IPv4 fixo do pool (o de
  // sempre) ou DHCP da rede do provedor — este só existe onde `resolve-cluster` confirmou.
  const [wanMode, setWanMode] = useState<WanMode>(WAN_MODE_POOL)
  // O cluster de destino NÃO é estado: ele é derivado da resposta de `resolve-cluster`
  // logo abaixo. Já foi um `useState` sincronizado por efeito, e era o defeito da #1084 —
  // ver o comentário na derivação.

  // VPC (network pool) selection — obrigatorio quando o cliente tem >1 VPC no cluster
  const [selectedNetworkPoolId, setSelectedNetworkPoolId] = useState<string | null>(null)

  // IP mode selection (VPC mode only)
  const [ipMode, setIpMode] = useState<'auto' | 'static_next' | 'static_specific'>('auto')
  const [staticIpAddress, setStaticIpAddress] = useState('')

  // Template selection (for products with multiple templates)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)

  // Resource selection for per_resource pricing
  const [selectedCpu, setSelectedCpu] = useState(0)
  const [selectedMemory, setSelectedMemory] = useState(0)
  const [selectedStorage, setSelectedStorage] = useState(0)

  // Cloud-init custom credentials
  const [showCloudInitConfig, setShowCloudInitConfig] = useState(false)
  const [cloudInitUser, setCloudInitUser] = useState('')
  const [cloudInitPassword, setCloudInitPassword] = useState('')
  const [showCloudInitPassword, setShowCloudInitPassword] = useState(false)

  // Alert modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean
    title: string
    message: string
    variant?: 'success' | 'error' | 'warning' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info',
  })

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { token } = useAuthStore()

  // Get reseller information
  const { data: resellerInfo } = useQuery<ResellerInfo>({
    queryKey: ['reseller-info'],
    enabled: !!token,
    queryFn: async () => {
      const data = await apiClient.get<ResellerInfo>('/api/v1/end-customer/reseller-info')

      return data
    },
  })

  // Get reseller config (payment methods enabled)
  const { data: resellerConfig } = useQuery<ResellerConfig>({
    queryKey: ['reseller-config'],
    enabled: !!token,
    queryFn: async () => {
      const data = await apiClient.get<ResellerConfig>('/api/v1/end-customer/reseller-config')

      return data
    },
  })

  const enabledPaymentMethods = resellerConfig?.payment_methods_enabled ?? {
    credit_card: true,
    credit_account: true,
    pix: false,
    boleto: false,
  }

  // Get active associations for reseller filter
  const { data: associationsData } = useQuery<AssociationsResponse>({
    queryKey: ['my-associations-active'],
    enabled: !!token,
    queryFn: async () => {
      const data = await apiClient.get<AssociationsResponse>(
        '/api/v1/end-customer/associations?status_filter=active',
      )

      return data
    },
  })

  const activeAssociations = associationsData?.associations || []

  // Mantem a busca do marketplace com o mesmo comportamento da lista de
  // instancias: filtra enquanto o usuario digita e persiste o termo na URL.
  // Uma nova busca sempre volta para a primeira pagina de resultados.
  useEffect(() => {
    const search = trimmedSearch || undefined
    setFilters((previousFilters) => {
      if (previousFilters.search === search) return previousFilters

      return { ...previousFilters, search, skip: 0 }
    })
  }, [trimmedSearch])

  // Get products from reseller
  const {
    data: productsData,
    isLoading,
    error,
  } = useQuery<ProductListResponse>({
    queryKey: ['end-customer-products', filters],
    enabled: !!token,
    // A busca atualiza os filtros e, portanto, a query. Sem manter os dados
    // anteriores, cada letra desmontava a tela durante o loading e o input
    // perdia foco. Instâncias já filtra localmente; aqui a paginação exige
    // consulta remota, então preservamos a mesma experiência de digitação.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.skip !== undefined) params.append('skip', filters.skip.toString())
      if (filters.limit !== undefined) params.append('limit', filters.limit.toString())
      if (filters.search) params.append('search', filters.search)
      if (filters.product_type) params.append('product_type', filters.product_type)
      if (filters.reseller_id) params.append('reseller_id', filters.reseller_id)

      const data = await apiClient.get<ProductListResponse>(
        `/api/v1/end-customer/products?${params.toString()}`,
      )

      return data
    },
  })

  // A lista principal pode estar paginada ou filtrada. A compra no modal da VM
  // precisa localizar o produto de IPv4 do mesmo reseller.
  const { data: ipv4ProductsData, isLoading: isLoadingIPv4Products } =
    useQuery<ProductListResponse>({
      queryKey: ['end-customer-ipv4-products', selectedProduct?.reseller_id],
      enabled:
        !!token &&
        showInstanceModal &&
        networkMode === 'public_bridge' &&
        !!selectedProduct?.reseller_id,
      queryFn: async () => {
        const params = new URLSearchParams({
          limit: '100',
          product_type: 'network',
          reseller_id: selectedProduct!.reseller_id,
        })
        return await apiClient.get<ProductListResponse>(
          `/api/v1/end-customer/products?${params.toString()}`,
        )
      },
    })

  const {
    data: resolvedInstanceClusterData,
    isPending: isResolvingInstanceCluster,
    isError: hasInstanceClusterResolutionError,
    refetch: refetchInstanceCluster,
  } = useQuery<ResolvedInstanceCluster>({
    queryKey: [
      'instance-target-cluster',
      selectedProduct?.id,
      selectedProduct?.reseller_id,
      selectedTemplateId,
    ],
    enabled: !!token && showInstanceModal && networkMode === 'public_bridge' && !!selectedProduct,
    queryFn: () =>
      apiClient.post<ResolvedInstanceCluster>('/api/v1/end-customer/instances/resolve-cluster', {
        product_id: selectedProduct!.id,
        reseller_id: selectedProduct!.reseller_id,
        vm_template_id: selectedTemplateId || undefined,
      }),
    // O destino NÃO se atualiza sozinho — é o que a cópia em estado dava de graça e some ao
    // derivar da query. O cliente compra o IPv4 para o cluster que esta resposta nomeou, e um
    // refetch no meio (voltar para a aba, reconectar) o mandaria comprar num cluster e criar
    // a VM noutro. Sai por `queryKey` (outro produto, outro template) ou pelo "Tentar
    // novamente", nunca por conta própria.
    staleTime: Infinity,
  })

  // O destino sai DIRETO da query, e é por isso que ele acompanha a troca de produto e de
  // template sem ninguém sincronizar nada: os três estão na `queryKey`. Antes isto era um
  // `useState` que um efeito copiava da query sob o guard `!resolvedInstanceClusterId`, e
  // cada ponto que zerava a cópia (fechar o modal, trocar de template, trocar o modo de
  // rede) abria uma janela em que a tela não tinha destino e a query não tinha o que
  // reentregar — o cache já estava quente, então nenhum dado NOVO chegava para acordar o
  // efeito, e a tela ficava presa em "Definindo o cluster de destino da VM..." (#1084).
  // Derivado não tem essa janela: onde havia cópia a preencher, agora há uma leitura.
  const resolvedInstanceClusterId = resolvedInstanceClusterData?.cluster_id ?? null

  const wanDhcpAvailable = resolvedInstanceClusterData?.wan_dhcp_available
  const opcoesDeEndereco = opcoesDeEnderecoPublico(wanDhcpAvailable)

  // O cluster de destino mudou (outro template, outro produto) e o novo não oferece DHCP:
  // a escolha volta ao pool em vez de sobreviver a uma resposta que a tirou do ar.
  useEffect(() => {
    setWanMode((atual) => modoValidoPara(atual, wanDhcpAvailable))
  }, [wanDhcpAvailable])

  // Get payment methods
  const { data: paymentMethodsData } = useQuery<PaymentMethodsResponse>({
    queryKey: ['payment-methods'],
    enabled: !!token,
    queryFn: async () => {
      const data = await apiClient.get<PaymentMethodsResponse>(
        '/api/v1/end-customer/payment-methods',
      )

      return data
    },
  })

  const paymentMethods = paymentMethodsData?.payment_methods || []

  // Get credit balance (for credit_account validation)
  const { data: creditBalanceData } = useQuery<CreditBalanceData | null>({
    queryKey: ['credit-balance'],
    enabled: !!token,
    queryFn: async () => {
      try {
        const data = await apiClient.get<CreditBalanceData>('/api/v1/end-customer/credit/balance')
        return data
      } catch {
        return null
      }
    },
  })

  // Fetch allocated public IPs for VM creation (public bridge mode)
  const {
    data: allocatedIpsData,
    isLoading: isLoadingAllocatedIps,
    isError: hasAllocatedIpsError,
    refetch: refetchAllocatedIps,
  } = useQuery({
    queryKey: ['allocated-public-ips', resolvedInstanceClusterId],
    enabled: !!token && networkMode === 'public_bridge' && !!resolvedInstanceClusterId,
    queryFn: async () => {
      const data = await publicIpApi.listAllocated(resolvedInstanceClusterId || undefined)
      return data
    },
  })

  // Check IPv4 stock
  const { data: ipv4StockData } = useQuery({
    queryKey: ['ipv4-stock-count'],
    enabled: !!token,
    queryFn: () => publicIpApi.countAvailable(),
  })
  const ipv4Available = ipv4StockData?.available ?? 0

  // Fetch customer VPCs for VM creation (VPC mode). Listadas globalmente; filtramos
  // pelo cluster do produto, pois o backend valida network_pool_id por cluster.
  const { data: vpcListData, isLoading: vpcLoading } = useQuery({
    queryKey: ['customer-vpcs-for-create'],
    enabled: !!token && showInstanceModal && networkMode === 'vpc',
    queryFn: () => vpcApi.listCustomerVPCs(),
  })

  const clusterVpcs = useMemo(() => {
    const all = vpcListData?.vpcs || []
    const cid = selectedProduct?.cluster_id
    return all.filter((v) => v.enabled && (!cid || v.cluster_id === cid))
  }, [vpcListData, selectedProduct?.cluster_id])

  // Pre-seleciona uma VPC (default com gateway > qualquer com gateway > primeira)
  // sempre que a lista muda e nao ha selecao valida.
  useEffect(() => {
    if (networkMode !== 'vpc') return
    if (selectedNetworkPoolId && clusterVpcs.some((v) => v.id === selectedNetworkPoolId)) return
    const preferred =
      clusterVpcs.find((v) => v.is_default && v.has_gateway) ||
      clusterVpcs.find((v) => v.has_gateway) ||
      clusterVpcs[0]
    setSelectedNetworkPoolId(preferred ? preferred.id : null)
  }, [clusterVpcs, networkMode, selectedNetworkPoolId])

  // Purchase IPv4 mutation
  const purchaseIPv4Mutation = useMutation({
    mutationFn: (data: {
      product_id: string
      payment_option: 'credit_card' | 'credit_account'
      payment_method_id?: string | null
      reseller_id?: string
      cluster_id?: string
    }) =>
      publicIpApi.purchase({
        product_id: data.product_id,
        payment_option: data.payment_option,
        payment_method_id: data.payment_method_id || undefined,
        reseller_id: data.reseller_id,
        // Na compra avulsa, `cluster_id` continua vazio: o cluster do produto IPv4
        // não é necessariamente o destino da VM. No fluxo dentro do modal da VM,
        // ele vem de `resolve-cluster`, então o IPv4 e a VM ficam no mesmo destino.
        cluster_id: data.cluster_id,
      }),
    onSuccess: (purchasedIp) => {
      setShowIPv4Modal(false)
      setSelectedIPv4Product(null)
      queryClient.invalidateQueries({ queryKey: ['ipv4-stock-count'] })
      queryClient.invalidateQueries({ queryKey: ['allocated-public-ips'] })

      if (isIPv4PurchaseForInstance) {
        setIsIPv4PurchaseForInstance(false)
        setSelectedPublicIpId(purchasedIp.ip_id)
        setAlertModal({
          isOpen: true,
          title: 'IPv4 Adquirido',
          message: `O IPv4 ${purchasedIp.ip_address} foi adquirido e já está selecionado para esta VM.`,
          variant: 'success',
        })
        return
      }

      setAlertModal({
        isOpen: true,
        title: 'IPv4 Adquirido',
        message:
          'Seu IPv4 foi adquirido com sucesso! Acesse a aba "IPs Públicos" em Networking para gerenciá-lo.',
        variant: 'success',
      })
    },
    onError: (error: any) => {
      // `data.detail` nunca existe: o handler global do backend reembrulha todo
      // HTTPException em `{ error: { message, ... } }`, entao a leitura antiga
      // caia sempre no `error.message` do axios e o cliente lia "Request failed
      // with status code 409" em vez do motivo. `getApiErrorText` é o helper do
      // repo para isso, e este arquivo ja o usa na mutation de criacao de VM.
      const errorMessage = getApiErrorText(error) ?? getErrorMessage(error)

      // 409 de ambiguidade (issue #691): o provedor tem IPv4 livre em mais de um
      // cluster e o backend recusa chutar, porque a VM so aceita anexar IP do
      // proprio cluster. Ele devolve os candidatos — o modal continua aberto e
      // pede a escolha, em vez de virar um erro sem acao possivel.
      const clusters = error?.response?.data?.error?.clusters as
        | Array<{ id: string; name: string }>
        | undefined

      if (error?.response?.status === 409 && clusters?.length) {
        setIpv4ClusterOptions(clusters)
        setSelectedIpv4ClusterId(clusters[0].id)
        return
      }

      setAlertModal({
        isOpen: true,
        title: 'Erro na Compra',
        message: errorMessage,
        variant: 'error',
      })
    },
  })

  // Create instance mutation
  const createInstanceMutation = useMutation({
    mutationFn: async ({
      productId,
      quantity,
      instanceNames: names,
      paymentOption,
      paymentMethodId,
      selectedCpu: cpu,
      selectedMemoryGb,
      selectedStorageGb,
      ipMode: mode,
      ipAddress,
      networkMode: netMode,
      publicIpAddressId,
      wanMode: wanModeEscolhido,
      cloudInitUser,
      cloudInitPassword,
      vmTemplateId,
      networkPoolId,
      confirmNoGateway,
      targetClusterId,
    }: {
      productId: string
      quantity: number
      instanceNames: string[]
      paymentOption: 'credit_card' | 'credit_account'
      paymentMethodId?: string | null
      selectedCpu?: number
      selectedMemoryGb?: number
      selectedStorageGb?: number
      ipMode?: string
      ipAddress?: string
      networkMode?: string
      publicIpAddressId?: string | null
      wanMode?: WanMode
      cloudInitUser?: string
      cloudInitPassword?: string
      vmTemplateId?: string | null
      networkPoolId?: string | null
      confirmNoGateway?: boolean
      targetClusterId?: string | null
    }) => {
      return await apiClient.post<RespostaDeCriacaoDeVm>(`/api/v1/end-customer/instances`, {
        product_id: productId,
        quantity,
        instance_names: names,
        payment_option: paymentOption,
        payment_method_id: paymentMethodId,
        reseller_id: selectedProduct?.reseller_id,
        selected_cpu: cpu,
        selected_memory_gb: selectedMemoryGb,
        selected_storage_gb: selectedStorageGb,
        ip_mode: netMode === 'vpc' ? mode || 'auto' : undefined,
        ip_address: mode === 'static_specific' ? ipAddress : undefined,
        network_mode: netMode || 'vpc',
        // `wan_mode` + `public_ip_address_id` saem da mesma régua (issue #1061): em DHCP o
        // IP NÃO vai, porque o backend recusa os dois juntos.
        ...camposDeRedePublica({
          networkMode: netMode === 'public_bridge' ? 'public_bridge' : 'vpc',
          wanMode: wanModeEscolhido ?? WAN_MODE_POOL,
          selectedPublicIpId: publicIpAddressId ?? null,
          wanDhcpAvailable,
        }),
        // Obrigatório quando o customer tem >1 VPC ativa no cluster — sem isso
        // o backend retorna 400 com a lista de opções. UI deve coletar via select.
        network_pool_id: networkMode === 'public_bridge' ? undefined : networkPoolId || undefined,
        // VPC sem Cloud Gateway: o backend recusa sem esta confirmação (issue
        // #727). O aviso já está na tela, colado no seletor de VPC — quem chega
        // aqui com uma VPC dangling escolhida acabou de lê-lo.
        confirm_no_gateway: confirmNoGateway ?? false,
        vm_template_id: vmTemplateId || undefined,
        target_cluster_id: targetClusterId || undefined,
        cloud_init_user: cloudInitUser || undefined,
        cloud_init_password: cloudInitPassword || undefined,
      })
    },
    onSuccess: (data) => {
      setShowInstanceModal(false)
      setInstanceQuantity(1)
      setInstanceNames([''])
      // `data` É o corpo da resposta: `apiClient.post` devolve `response.data`
      // (shared/services/api.ts), sem envelope. Os `warnings` que vêm nele (ex.:
      // VPC sem Cloud Gateway — a VM sobe sem DHCP nem internet, issue #727)
      // precisam chegar ao cliente; senão ele só descobre com a VM incomunicável.
      setAlertModal({ isOpen: true, ...montarAlertaDeCriacao(data, instanceQuantity) })
    },
    onError: (error: any) => {
      console.error('❌ Error creating instance:', error)
      // Captura no Sentry: erros aqui sao tratados (viram AlertModal), entao nao
      // chegam como uncaught. Sem isto, falhas de criacao de VM ficam invisiveis.
      Sentry.captureException(error, { tags: { feature: 'vm-create' } })

      // O roteamento (e sobretudo a ORDEM das regras) vive em função pura, com
      // teste — a recusa por `code` tem de vencer as heurísticas de texto.
      const acao = decidirAcaoDeErroDeCriacao(error)
      if (acao.tipo === 'abrirCriarVpc') {
        setShowCreateVPCModal(true)
        return
      }
      if (acao.recarregarVpcs) {
        queryClient.invalidateQueries({ queryKey: ['customer-vpcs-for-create'] })
      }
      setAlertModal({
        isOpen: true,
        title: acao.title,
        message: acao.message,
        variant: acao.variant,
      })
    },
  })

  // Handle filter changes
  const handleFilterChange = (key: keyof ProductListFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value, skip: 0 }))
  }

  // Handle load more
  const handleLoadMore = () => {
    if (productsData?.has_more) {
      setFilters((prev) => ({
        ...prev,
        skip: (prev.skip || 0) + (prev.limit || 20),
      }))
    }
  }

  // Handle view product
  const handleView = (product: Product) => {
    setSelectedProduct(product)
    setShowViewModal(true)
  }

  // Handle purchase IPv4
  const handlePurchaseIPv4 = (
    product: Product,
    forInstance = false,
    clusterId: string | null = null,
  ) => {
    setSelectedIPv4Product(product)
    setIsIPv4PurchaseForInstance(forInstance)
    setIpv4ClusterOptions([])
    setSelectedIpv4ClusterId(clusterId)

    // Auto-select payment option
    if (enabledPaymentMethods.credit_card && !enabledPaymentMethods.credit_account) {
      setPaymentOption('credit_card')
    } else if (enabledPaymentMethods.credit_account && !enabledPaymentMethods.credit_card) {
      setPaymentOption('credit_account')
    } else {
      setPaymentOption('credit_card')
    }

    const defaultPaymentMethod = paymentMethods.find((pm: any) => pm.is_default && pm.is_active)
    if (defaultPaymentMethod) {
      setSelectedPaymentMethodId(defaultPaymentMethod.id)
    }

    setShowIPv4Modal(true)
  }

  const closeIPv4PurchaseModal = () => {
    setShowIPv4Modal(false)
    setSelectedIPv4Product(null)
    setIsIPv4PurchaseForInstance(false)
    setIpv4ClusterOptions([])
    setSelectedIpv4ClusterId(null)
  }

  // Handle create instance
  const handleCreateInstance = (product: Product) => {
    setSelectedProduct(product)
    setNetworkMode('vpc')
    setSelectedPublicIpId(null)
    const baseName = `${product.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`
    setInstanceNames([baseName])

    // Initialize resource selections for per_resource products
    if (product.pricing_model === 'per_resource') {
      setSelectedCpu(product.min_cpu || 1)
      setSelectedMemory(Math.ceil((product.min_memory || 1024) / 1024))
      setSelectedStorage(product.min_storage || 10)
    }

    // Auto-select payment option based on reseller config
    if (enabledPaymentMethods.credit_card && !enabledPaymentMethods.credit_account) {
      setPaymentOption('credit_card')
    } else if (enabledPaymentMethods.credit_account && !enabledPaymentMethods.credit_card) {
      setPaymentOption('credit_account')
    } else {
      setPaymentOption('credit_card')
    }

    // Set default payment method if available
    const defaultPaymentMethod = paymentMethods.find((pm: any) => pm.is_default && pm.is_active)
    if (defaultPaymentMethod) {
      setSelectedPaymentMethodId(defaultPaymentMethod.id)
    }

    // Auto-select default template
    if (product.available_templates && product.available_templates.length > 0) {
      const defaultTpl =
        product.available_templates.find((t) => t.is_default) || product.available_templates[0]
      setSelectedTemplateId(defaultTpl.id)
    } else {
      setSelectedTemplateId(null)
    }

    setShowInstanceModal(true)
  }

  // Calculate per-resource price in real-time
  const calculatedPrice = useMemo(() => {
    if (!selectedProduct || selectedProduct.pricing_model !== 'per_resource') return null
    const base = selectedProduct.base_price || 0
    const cpuCost = (selectedProduct.price_per_cpu || 0) * selectedCpu
    const memoryCost = (selectedProduct.price_per_memory_gb || 0) * selectedMemory
    const storageCost = (selectedProduct.price_per_storage_gb || 0) * selectedStorage
    return {
      base,
      cpuCost,
      memoryCost,
      storageCost,
      total: base + cpuCost + memoryCost + storageCost,
    }
  }, [selectedProduct, selectedCpu, selectedMemory, selectedStorage])

  const ipv4ProductForSelectedInstance = useMemo(() => {
    if (!selectedProduct) return null

    const ipv4Products = [...(ipv4ProductsData?.products || []), ...(productsData?.products || [])]

    return (
      ipv4Products.find(
        (product) =>
          product.product_type === 'network' &&
          product.reseller_id === selectedProduct.reseller_id &&
          (!product.cluster_id || product.cluster_id === resolvedInstanceClusterId),
      ) || null
    )
  }, [
    ipv4ProductsData?.products,
    productsData?.products,
    resolvedInstanceClusterId,
    selectedProduct,
  ])

  // Submit instance creation
  const handleSubmitInstance = () => {
    if (!selectedProduct || instanceNames.some((n) => !n.trim())) return

    // Validate payment option
    if (paymentOption === 'credit_card' && !selectedPaymentMethodId) {
      setAlertModal({
        isOpen: true,
        title: 'Método de Pagamento Obrigatório',
        message: 'Por favor, selecione um método de pagamento ou escolha pagar com crédito.',
        variant: 'warning',
      })
      return
    }

    createInstanceMutation.mutate({
      productId: selectedProduct.id,
      quantity: instanceQuantity,
      instanceNames: instanceNames.map((n) => n.trim()),
      paymentOption: paymentOption,
      paymentMethodId: selectedPaymentMethodId,
      ipMode: ipMode,
      ipAddress: ipMode === 'static_specific' ? staticIpAddress : undefined,
      networkMode: networkMode,
      publicIpAddressId: networkMode === 'public_bridge' ? selectedPublicIpId : undefined,
      wanMode: networkMode === 'public_bridge' ? wanMode : undefined,
      networkPoolId: networkMode === 'vpc' ? selectedNetworkPoolId : undefined,
      // Assimetria deliberada com o modal de mover (que exige checkbox): aqui o
      // aviso de "sem gateway" já está colado no seletor de VPC e o cliente
      // acabou de escolhê-la de olhos abertos, então a flag acompanha a escolha.
      // Exigir um segundo gesto na compra também divergiria do caminho de VPC
      // única, que o backend deixa passar só avisando.
      confirmNoGateway:
        networkMode === 'vpc' &&
        clusterVpcs.find((v) => v.id === selectedNetworkPoolId)?.has_gateway === false,
      targetClusterId: networkMode === 'public_bridge' ? resolvedInstanceClusterId : undefined,
      cloudInitUser: cloudInitUser.trim() || undefined,
      cloudInitPassword: cloudInitPassword || undefined,
      vmTemplateId: selectedTemplateId,
      ...(selectedProduct.pricing_model === 'per_resource'
        ? {
            selectedCpu: selectedCpu,
            selectedMemoryGb: selectedMemory,
            selectedStorageGb: selectedStorage,
          }
        : {}),
    })
  }

  // Close modals
  const closeModals = () => {
    setShowViewModal(false)
    setShowInstanceModal(false)
    setNetworkMode('vpc')
    setSelectedPublicIpId(null)
    setWanMode(WAN_MODE_POOL)
    setSelectedIPv4Product(null)
    setIsIPv4PurchaseForInstance(false)
    setIpv4ClusterOptions([])
    setSelectedIpv4ClusterId(null)
    setSelectedProduct(null)
    setInstanceQuantity(1)
    setInstanceNames([''])
    setPaymentOption('credit_card')
    setSelectedPaymentMethodId(null)
    setIpMode('auto')
    setStaticIpAddress('')
    setSelectedNetworkPoolId(null)
    setShowCloudInitConfig(false)
    setCloudInitUser('')
    setCloudInitPassword('')
    setShowCloudInitPassword(false)
  }

  // Format currency
  const formatCurrency = (value: number, currency: string = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency,
    }).format(value)
  }

  // Get product type label
  const getProductTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      vm: 'Máquina Virtual',
      storage: 'Armazenamento',
      block_storage: 'Block Storage',
      backup: 'Backup',
      load_balancer: 'Load Balancer',
      firewall: 'Firewall',
      network: 'Rede',
      bundle: 'Pacote',
    }
    return types[type] || type
  }

  // Get billing cycle label
  const getBillingCycleLabel = (cycle: string) => {
    const cycles: Record<string, string> = {
      hourly: 'Por hora',
      daily: 'Diário',
      monthly: 'Mensal',
      annually: 'Anual',
    }
    return cycles[cycle] || cycle
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <PackageIcon className="mx-auto mb-4 h-8 w-8 animate-pulse text-blue-600" />
          <p className="text-gray-600">Carregando marketplace...</p>
        </div>
      </div>
    )
  }

  if (error) {
    console.error('Products query error:', error)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <PackageIcon className="mx-auto mb-4 h-16 w-16 text-red-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Erro ao carregar marketplace</h3>
          <p className="mb-4 text-gray-600">
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="mobile-page-padding">
      <ResponsivePageHeader
        title="Marketplace"
        description="Explore e contrate produtos e serviços disponíveis"
        icon={ShoppingCartIcon}
        actions={
          <ResponsiveActionRow justify="end" className="w-full sm:w-auto">
            <div className="inline-flex items-center space-x-2 text-sm text-gray-500">
              <ShoppingCartIcon className="h-4 w-4" />
              <span>Portal do Cliente</span>
            </div>
          </ResponsiveActionRow>
        }
        meta={
          resellerInfo?.reseller ? (
            <div className="mt-1 flex items-center space-x-2 text-sm">
              <BuildingIcon className="h-4 w-4 text-blue-600" />
              <span className="text-gray-600">Fornecido por:</span>
              <span className="font-medium text-blue-600">{resellerInfo.reseller.name}</span>
            </div>
          ) : undefined
        }
      />

      {/* Search and Filters */}
      <div className="mb-6 rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <SearchIcon className="absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar no marketplace..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-2.5 pr-4 pl-10 transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center justify-center space-x-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            <FilterIcon className="h-4 w-4" />
            <span>Filtros</span>
          </button>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-4 grid grid-cols-1 gap-4 border-t border-slate-200 pt-4 md:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold tracking-wider text-slate-500 uppercase">
                Tipo
              </label>
              <select
                value={filters.product_type || ''}
                onChange={(e) => handleFilterChange('product_type', e.target.value || undefined)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos os tipos</option>
                <option value="vm">Máquina Virtual</option>
                <option value="storage">Armazenamento</option>
                <option value="backup">Backup</option>
                <option value="load_balancer">Load Balancer</option>
                <option value="firewall">Firewall</option>
                <option value="network">Rede</option>
                <option value="bundle">Pacote</option>
              </select>
            </div>
            {activeAssociations.length > 1 && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold tracking-wider text-slate-500 uppercase">
                  Reseller
                </label>
                <select
                  value={filters.reseller_id || ''}
                  onChange={(e) => handleFilterChange('reseller_id', e.target.value || undefined)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Todos os resellers</option>
                  {activeAssociations.map((assoc: any) => (
                    <option key={assoc.id} value={assoc.reseller_id}>
                      {assoc.reseller?.name || assoc.reseller_id}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Products Grid */}
      <InfraSetupBanner infraStatus={infraStatus} />

      {productsData && productsData.products.length > 0 ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
            {productsData.products.map((product) => (
              <div
                key={product.id}
                className="group flex h-full flex-col rounded-2xl border border-slate-200/60 bg-white shadow-sm transition-all hover:border-slate-300/60 hover:shadow-md"
              >
                {/* Card Header */}
                <div className="p-5 pb-4">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <h3 className="mb-0.5 text-base font-semibold text-slate-900">
                        {product.name}
                      </h3>
                      <p className="text-xs font-medium tracking-wider text-slate-500 uppercase">
                        {getProductTypeLabel(product.product_type)}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-lg border border-emerald-200/60 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700">
                      <CheckCircleIcon className="h-3 w-3" />
                      Disponível
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                    <BuildingIcon className="h-3 w-3" />
                    <span>{product.reseller_name}</span>
                  </div>
                </div>

                {/* Card Content */}
                <div className="space-y-3 px-5 pb-4">
                  {/* Description */}
                  {product.description && (
                    <p className="line-clamp-2 text-sm leading-relaxed text-slate-500">
                      {product.description}
                    </p>
                  )}

                  {/* Resource Requirements */}
                  {!!(
                    product.requires_cpu ||
                    product.requires_memory ||
                    product.requires_storage
                  ) && (
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      {!!product.requires_cpu && (
                        <div className="flex items-center gap-1">
                          <CpuIcon className="h-3.5 w-3.5 text-blue-500" />
                          <span className="font-medium">{product.requires_cpu} cores</span>
                        </div>
                      )}
                      {!!product.requires_memory && (
                        <div className="flex items-center gap-1">
                          <DatabaseIcon className="h-3.5 w-3.5 text-violet-500" />
                          <span className="font-medium">{product.requires_memory} GB</span>
                        </div>
                      )}
                      {!!product.requires_storage && (
                        <div className="flex items-center gap-1">
                          <HardDriveIcon className="h-3.5 w-3.5 text-amber-500" />
                          <span className="font-medium">{product.requires_storage} GB</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tags */}
                  {product.tags && product.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {product.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
                        >
                          {tag}
                        </span>
                      ))}
                      {product.tags.length > 3 && (
                        <span className="text-[11px] text-slate-400">
                          +{product.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Available templates badge */}
                  {product.available_templates && product.available_templates.length > 1 && (
                    <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] font-medium text-slate-600">
                      <ServerIcon className="h-3 w-3 text-slate-400" />
                      <span>{product.available_templates.length} templates disponíveis</span>
                    </div>
                  )}
                </div>

                {/* Pricing */}
                <div className="mx-5 mt-auto mb-4 min-h-[80px] rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 to-slate-100/50 p-4">
                  <div className="flex items-end justify-between">
                    <div>
                      {product.pricing_model === 'per_resource' ? (
                        <>
                          <p className="text-xl font-bold tracking-tight text-slate-900">
                            {product.min_estimated_price != null ? (
                              <>
                                A partir de{' '}
                                {formatCurrency(product.min_estimated_price, product.currency)}
                              </>
                            ) : (
                              formatCurrency(product.base_price, product.currency)
                            )}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {getBillingCycleLabel(product.billing_cycle)}
                            <span className="ml-1.5 rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider text-blue-600 uppercase">
                              Configurável
                            </span>
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-xl font-bold tracking-tight text-slate-900">
                            {formatCurrency(product.base_price, product.currency)}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {getBillingCycleLabel(product.billing_cycle)}
                          </p>
                        </>
                      )}
                    </div>
                    {!!(product.setup_fee && product.setup_fee > 0) && (
                      <div className="text-right">
                        <p className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
                          Setup
                        </p>
                        <p className="text-sm font-semibold text-slate-700">
                          {formatCurrency(product.setup_fee, product.currency)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Actions */}
                <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3.5">
                  <button
                    onClick={() => handleView(product)}
                    className="inline-flex items-center space-x-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-800"
                    title="Ver detalhes"
                  >
                    <EyeIcon className="h-4 w-4" />
                    <span>Detalhes</span>
                  </button>

                  {product.product_type === 'network' ? (
                    <button
                      onClick={() => handlePurchaseIPv4(product)}
                      disabled={ipv4Available === 0}
                      className={`inline-flex items-center space-x-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white transition-all ${ipv4Available === 0 ? 'cursor-not-allowed bg-slate-300' : 'bg-orange-500 shadow-sm shadow-orange-200 hover:bg-orange-600'}`}
                      title={ipv4Available === 0 ? 'Sem estoque de IPv4' : 'Comprar IPv4'}
                    >
                      <GlobeIcon className="h-4 w-4" />
                      <span>{ipv4Available === 0 ? 'Sem Estoque' : 'Comprar IPv4'}</span>
                    </button>
                  ) : product.product_type === 'block_storage' ? (
                    <button
                      onClick={() => navigate('/storage')}
                      className="inline-flex items-center space-x-1.5 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-200 transition-all hover:bg-violet-700"
                      title="Criar volume"
                    >
                      <HardDriveIcon className="h-4 w-4" />
                      <span>Criar Volume</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handleCreateInstance(product)}
                      disabled={
                        !product.available_templates || product.available_templates.length === 0
                      }
                      className={`inline-flex items-center space-x-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white transition-all ${
                        !product.available_templates || product.available_templates.length === 0
                          ? 'cursor-not-allowed bg-slate-300'
                          : 'bg-gradient-to-r from-emerald-600 to-emerald-500 shadow-sm shadow-emerald-200 hover:from-emerald-700 hover:to-emerald-600'
                      }`}
                      title={
                        !product.available_templates || product.available_templates.length === 0
                          ? 'Produto sem template configurado'
                          : 'Criar instância'
                      }
                    >
                      <PlayIcon className="h-4 w-4" />
                      <span>
                        {!product.available_templates || product.available_templates.length === 0
                          ? 'Sem Template'
                          : 'Criar VM'}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Load More */}
          {productsData.has_more && (
            <div className="text-center">
              <button
                onClick={handleLoadMore}
                className="rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-800"
              >
                Carregar Mais
              </button>
            </div>
          )}

          {/* Results Info */}
          <div className="text-center text-xs font-medium text-slate-400">
            Mostrando {productsData.products.length} de {productsData.total} itens
          </div>
        </div>
      ) : (
        <div className="py-16 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
            <PackageIcon className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="mb-1 text-base font-semibold text-slate-800">Nenhum item disponível</h3>
          <p className="text-sm text-slate-500">
            {resellerInfo?.reseller
              ? `Aguarde novos itens de ${resellerInfo.reseller.name}`
              : 'Aguarde a configuração do seu provedor'}
          </p>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10">
                    <PackageIcon className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">{selectedProduct.name}</h2>
                    <p className="text-sm text-slate-400">{selectedProduct.reseller_name}</p>
                  </div>
                </div>
                <button
                  onClick={closeModals}
                  className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="space-y-6 p-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <h3 className="mb-4 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                    Informações do Produto
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="mb-0.5 block text-xs font-medium text-slate-400">
                        Tipo
                      </label>
                      <p className="text-sm font-medium text-slate-800">
                        {getProductTypeLabel(selectedProduct.product_type)}
                      </p>
                    </div>
                    {selectedProduct.description && (
                      <div>
                        <label className="mb-0.5 block text-xs font-medium text-slate-400">
                          Descrição
                        </label>
                        <p className="text-sm leading-relaxed text-slate-700">
                          {selectedProduct.description}
                        </p>
                      </div>
                    )}
                    {selectedProduct.features && selectedProduct.features.length > 0 && (
                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-400">
                          Características
                        </label>
                        <ul className="space-y-1 text-sm text-slate-700">
                          {selectedProduct.features.map((feature, index) => (
                            <li key={index} className="flex items-center gap-2">
                              <CheckCircleIcon className="h-3.5 w-3.5 flex-shrink-0 text-emerald-500" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="mb-4 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                    Preço e Cobrança
                  </h3>
                  <div className="space-y-3">
                    <div className="rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 to-slate-100/50 p-4">
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Preço Final
                      </label>
                      {selectedProduct.pricing_model === 'per_resource' ? (
                        <>
                          <p className="text-2xl font-bold text-blue-700">
                            {selectedProduct.min_estimated_price != null ? (
                              <>
                                A partir de{' '}
                                {formatCurrency(
                                  selectedProduct.min_estimated_price,
                                  selectedProduct.currency,
                                )}
                              </>
                            ) : (
                              formatCurrency(selectedProduct.base_price, selectedProduct.currency)
                            )}
                          </p>
                          <p className="mt-1 text-sm text-blue-600">
                            {getBillingCycleLabel(selectedProduct.billing_cycle)}
                            <span className="ml-2 rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-700">
                              Configurável
                            </span>
                          </p>
                          <div className="mt-2 space-y-1 text-xs text-blue-700">
                            <div className="flex justify-between">
                              <span>Taxa base:</span>
                              <span>
                                {formatCurrency(
                                  selectedProduct.base_price,
                                  selectedProduct.currency,
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Preço/vCPU:</span>
                              <span>
                                {formatCurrency(
                                  selectedProduct.price_per_cpu || 0,
                                  selectedProduct.currency,
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Preço/GB RAM:</span>
                              <span>
                                {formatCurrency(
                                  selectedProduct.price_per_memory_gb || 0,
                                  selectedProduct.currency,
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Preço/GB Storage:</span>
                              <span>
                                {formatCurrency(
                                  selectedProduct.price_per_storage_gb || 0,
                                  selectedProduct.currency,
                                )}
                              </span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-2xl font-bold text-blue-700">
                            {formatCurrency(selectedProduct.base_price, selectedProduct.currency)}
                          </p>
                          <p className="mt-1 text-sm text-blue-600">
                            {getBillingCycleLabel(selectedProduct.billing_cycle)}
                          </p>
                        </>
                      )}
                    </div>

                    {!!(selectedProduct.setup_fee && selectedProduct.setup_fee > 0) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Taxa de Setup
                        </label>
                        <p className="font-semibold text-gray-900">
                          {formatCurrency(selectedProduct.setup_fee, selectedProduct.currency)}
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Quantidade</label>
                      <p className="text-gray-900">
                        Mín: {selectedProduct.min_quantity || 1}
                        {selectedProduct.max_quantity && ` | Máx: ${selectedProduct.max_quantity}`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resource Requirements */}
              {!!(
                selectedProduct.requires_cpu ||
                selectedProduct.requires_memory ||
                selectedProduct.requires_storage
              ) && (
                <div>
                  <h3 className="mb-4 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                    Especificações Técnicas
                  </h3>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    {!!selectedProduct.requires_cpu && (
                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="mb-2 flex items-center gap-2">
                          <CpuIcon className="h-4 w-4 text-blue-500" />
                          <span className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
                            CPU
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-slate-900">
                          {selectedProduct.requires_cpu}
                        </p>
                        <p className="text-xs text-slate-500">cores</p>
                      </div>
                    )}
                    {!!selectedProduct.requires_memory && (
                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="mb-2 flex items-center gap-2">
                          <DatabaseIcon className="h-4 w-4 text-violet-500" />
                          <span className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
                            Memória
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-slate-900">
                          {selectedProduct.requires_memory}
                        </p>
                        <p className="text-xs text-slate-500">GB</p>
                      </div>
                    )}
                    {!!selectedProduct.requires_storage && (
                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="mb-2 flex items-center gap-2">
                          <HardDriveIcon className="h-4 w-4 text-amber-500" />
                          <span className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
                            Armazenamento
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-slate-900">
                          {selectedProduct.requires_storage}
                        </p>
                        <p className="text-xs text-slate-500">GB</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tags */}
              {selectedProduct.tags && selectedProduct.tags.length > 0 && (
                <div>
                  <h3 className="mb-3 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProduct.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-slate-200 bg-slate-50/80 px-6 py-4">
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  onClick={closeModals}
                  className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
                >
                  Fechar
                </button>
                {selectedProduct.product_type === 'block_storage' ? (
                  <button
                    onClick={() => {
                      closeModals()
                      navigate('/storage')
                    }}
                    className="flex items-center gap-2 rounded-xl bg-violet-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm shadow-violet-200 transition-all hover:bg-violet-700"
                  >
                    <HardDriveIcon className="h-4 w-4" />
                    Criar Volume
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      closeModals()
                      handleCreateInstance(selectedProduct)
                    }}
                    className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm shadow-emerald-200 transition-all hover:from-emerald-700 hover:to-emerald-600"
                  >
                    <PlayIcon className="h-4 w-4" />
                    Criar VM
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instance Creation Modal */}
      {showInstanceModal && selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            {/* Header - Fixed */}
            <div className="flex-shrink-0 bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10">
                    <ServerIcon className="h-5 w-5 text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Criar Nova VM</h2>
                    <p className="text-sm text-slate-400">
                      {selectedProduct.name}
                      {selectedProduct.description ? ` — ${selectedProduct.description}` : ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeModals}
                  className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                <div className="space-y-5 xl:col-span-8">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <div className="lg:col-span-2">
                      <label className="mb-1.5 block text-xs font-semibold tracking-wider text-slate-500 uppercase">
                        {instanceQuantity > 1 ? 'Nome base das VMs' : 'Nome da VM'}
                      </label>
                      <input
                        type="text"
                        value={instanceNames[0] || ''}
                        onChange={(e) => {
                          const baseName = e.target.value
                          if (instanceQuantity <= 1) {
                            setInstanceNames([baseName])
                          } else {
                            setInstanceNames(
                              Array.from({ length: instanceQuantity }, (_, i) =>
                                baseName ? `${baseName}-${i + 1}` : '',
                              ),
                            )
                          }
                        }}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2.5 transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                        placeholder="ex: Meu Servidor Web"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-semibold tracking-wider text-slate-500 uppercase">
                        Quantidade
                      </label>
                      <input
                        type="number"
                        min={selectedProduct.min_quantity || 1}
                        max={selectedProduct.max_quantity || undefined}
                        value={instanceQuantity}
                        onChange={(e) => {
                          const qty = Math.max(1, Number(e.target.value) || 1)
                          setInstanceQuantity(qty)
                          const baseName = instanceNames[0]?.replace(/-\d+$/, '') || ''
                          if (qty <= 1) {
                            setInstanceNames([baseName])
                          } else {
                            setInstanceNames(
                              Array.from({ length: qty }, (_, i) =>
                                baseName ? `${baseName}-${i + 1}` : '',
                              ),
                            )
                          }
                        }}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2.5 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Individual VM names when quantity > 1 */}
                  {instanceQuantity > 1 && (
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold tracking-wider text-slate-500 uppercase">
                        Nomes individuais
                      </label>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {instanceNames.map((name, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="w-10 flex-shrink-0 text-xs font-medium text-slate-400">
                              VM {idx + 1}
                            </span>
                            <input
                              type="text"
                              value={name}
                              onChange={(e) => {
                                const updated = [...instanceNames]
                                updated[idx] = e.target.value
                                setInstanceNames(updated)
                              }}
                              className="flex-1 rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                              placeholder={`Servidor ${idx + 1}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Template Selection (for products with multiple templates) */}
                  {selectedProduct.available_templates &&
                    selectedProduct.available_templates.length > 1 && (
                      <div className="space-y-3">
                        <h4 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
                          Sistema Operacional
                        </h4>
                        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                          {selectedProduct.available_templates.map((tpl) => {
                            const isSelected = selectedTemplateId === tpl.id
                            return (
                              <label
                                key={tpl.id}
                                className={`relative flex cursor-pointer items-center gap-3 rounded-xl border-2 p-3.5 transition-all ${
                                  isSelected
                                    ? 'border-blue-500 bg-blue-50/60 shadow-sm ring-1 ring-blue-200'
                                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/50'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="vm_template"
                                  value={tpl.id}
                                  checked={isSelected}
                                  onChange={() => {
                                    setSelectedTemplateId(tpl.id)
                                    // O cluster acompanha sozinho: o template está na
                                    // `queryKey` de `resolve-cluster`. Só o IP escolhido
                                    // precisa cair — ele é do cluster anterior.
                                    setSelectedPublicIpId(null)
                                  }}
                                  className="sr-only"
                                />
                                <div
                                  className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                                    isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                                  }`}
                                >
                                  {isSelected && <CheckCircleIcon className="h-3 w-3 text-white" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-slate-900">
                                      {tpl.name}
                                    </span>
                                  </div>
                                  {tpl.is_default && (
                                    <span className="mt-1 inline-block rounded-md bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider text-blue-700 uppercase">
                                      Recomendado
                                    </span>
                                  )}
                                </div>
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    )}

                  {/* Single template info (informative) */}
                  {selectedProduct.available_templates &&
                    selectedProduct.available_templates.length === 1 && (
                      <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-sm text-slate-600">
                        <ServerIcon className="h-4 w-4 text-slate-400" />
                        <span>
                          Sistema Operacional:{' '}
                          <strong className="text-slate-800">
                            {selectedProduct.available_templates[0].name}
                          </strong>
                        </span>
                      </div>
                    )}

                  {/* No template warning */}
                  {(!selectedProduct.available_templates ||
                    selectedProduct.available_templates.length === 0) && (
                    <div className="flex items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-700">
                      <AlertTriangleIcon className="h-4 w-4 flex-shrink-0 text-amber-500" />
                      <span>
                        Este produto não possui template configurado. Não é possível criar uma VM.
                      </span>
                    </div>
                  )}

                  {/* Resource Selection for per_resource products */}
                  {selectedProduct.pricing_model === 'per_resource' && (
                    <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/30 p-5">
                      <h4 className="flex items-center gap-2 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                        <CpuIcon className="h-3.5 w-3.5" />
                        Configuração de Recursos
                      </h4>

                      {/* CPU Slider */}
                      <div>
                        <div className="mb-2 flex items-center justify-between text-sm">
                          <label className="flex items-center gap-1.5 font-medium text-slate-700">
                            <CpuIcon className="h-3.5 w-3.5 text-blue-500" />
                            vCPUs
                          </label>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min={selectedProduct.min_cpu || 1}
                              max={selectedProduct.max_cpu || 32}
                              step={1}
                              value={selectedCpu}
                              onChange={(e) => {
                                const v = Number(e.target.value)
                                const min = selectedProduct.min_cpu || 1
                                const max = selectedProduct.max_cpu || 32
                                if (!isNaN(v)) setSelectedCpu(Math.min(max, Math.max(min, v)))
                              }}
                              className="w-16 rounded-lg border border-slate-200 bg-white px-2 py-1 text-center font-semibold text-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="w-10 text-xs font-medium text-slate-500">cores</span>
                          </div>
                        </div>
                        <input
                          type="range"
                          min={selectedProduct.min_cpu || 1}
                          max={selectedProduct.max_cpu || 32}
                          step={1}
                          value={selectedCpu}
                          onChange={(e) => setSelectedCpu(Number(e.target.value))}
                          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
                        />
                        <div className="mt-1 flex justify-between text-[11px] text-slate-400">
                          <span>{selectedProduct.min_cpu || 1}</span>
                          <span>{selectedProduct.max_cpu || 32}</span>
                        </div>
                      </div>

                      {/* Memory Slider (min/max stored in MB, slider works in GB) */}
                      <div>
                        <div className="mb-2 flex items-center justify-between text-sm">
                          <label className="flex items-center gap-1.5 font-medium text-slate-700">
                            <DatabaseIcon className="h-3.5 w-3.5 text-violet-500" />
                            Memória RAM
                          </label>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min={Math.ceil((selectedProduct.min_memory || 1024) / 1024)}
                              max={Math.ceil((selectedProduct.max_memory || 131072) / 1024)}
                              step={1}
                              value={selectedMemory}
                              onChange={(e) => {
                                const v = Number(e.target.value)
                                const min = Math.ceil((selectedProduct.min_memory || 1024) / 1024)
                                const max = Math.ceil((selectedProduct.max_memory || 131072) / 1024)
                                if (!isNaN(v)) setSelectedMemory(Math.min(max, Math.max(min, v)))
                              }}
                              className="w-16 rounded-lg border border-slate-200 bg-white px-2 py-1 text-center font-semibold text-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="w-10 text-xs font-medium text-slate-500">GB</span>
                          </div>
                        </div>
                        <input
                          type="range"
                          min={Math.ceil((selectedProduct.min_memory || 1024) / 1024)}
                          max={Math.ceil((selectedProduct.max_memory || 131072) / 1024)}
                          step={1}
                          value={selectedMemory}
                          onChange={(e) => setSelectedMemory(Number(e.target.value))}
                          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-violet-600"
                        />
                        <div className="mt-1 flex justify-between text-[11px] text-slate-400">
                          <span>{Math.ceil((selectedProduct.min_memory || 1024) / 1024)} GB</span>
                          <span>{Math.ceil((selectedProduct.max_memory || 131072) / 1024)} GB</span>
                        </div>
                      </div>

                      {/* Storage Slider */}
                      <div>
                        <div className="mb-2 flex items-center justify-between text-sm">
                          <label className="flex items-center gap-1.5 font-medium text-slate-700">
                            <HardDriveIcon className="h-3.5 w-3.5 text-amber-500" />
                            Storage SSD
                          </label>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min={selectedProduct.min_storage || 10}
                              max={selectedProduct.max_storage || 2000}
                              step={10}
                              value={selectedStorage}
                              onChange={(e) => {
                                const v = Number(e.target.value)
                                const min = selectedProduct.min_storage || 10
                                const max = selectedProduct.max_storage || 2000
                                if (!isNaN(v)) setSelectedStorage(Math.min(max, Math.max(min, v)))
                              }}
                              className="w-20 rounded-lg border border-slate-200 bg-white px-2 py-1 text-center font-semibold text-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="w-10 text-xs font-medium text-slate-500">GB</span>
                          </div>
                        </div>
                        <input
                          type="range"
                          min={selectedProduct.min_storage || 10}
                          max={selectedProduct.max_storage || 2000}
                          step={10}
                          value={selectedStorage}
                          onChange={(e) => setSelectedStorage(Number(e.target.value))}
                          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-amber-500"
                        />
                        <div className="mt-1 flex justify-between text-[11px] text-slate-400">
                          <span>{selectedProduct.min_storage || 10} GB</span>
                          <span>{selectedProduct.max_storage || 2000} GB</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Network Mode Selection */}
                  <div className="space-y-3 rounded-xl border border-slate-200 p-5">
                    <label className="block flex items-center gap-2 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                      <GlobeIcon className="h-3.5 w-3.5" />
                      Conexão de Rede
                    </label>
                    <div className="space-y-2">
                      <label
                        className={`flex cursor-pointer items-start space-x-3 rounded-lg border-2 p-3 transition-colors ${
                          networkMode === 'vpc'
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => {
                          setNetworkMode('vpc')
                          setSelectedPublicIpId(null)
                          setWanMode(WAN_MODE_POOL)
                        }}
                      >
                        <input
                          type="radio"
                          name="networkMode"
                          value="vpc"
                          checked={networkMode === 'vpc'}
                          onChange={() => setNetworkMode('vpc')}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            VPC — Rede Privada (Recomendado)
                          </p>
                          <p className="text-xs text-gray-500">
                            Sua VM fica isolada em rede privada com acesso a internet via Gateway.
                          </p>
                        </div>
                      </label>
                      <label
                        className={`flex cursor-pointer items-start space-x-3 rounded-lg border-2 p-3 transition-colors ${
                          networkMode === 'public_bridge'
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => {
                          setNetworkMode('public_bridge')
                          setIpMode('auto')
                          setSelectedPublicIpId(null)
                          setWanMode(WAN_MODE_POOL)
                        }}
                      >
                        <input
                          type="radio"
                          name="networkMode"
                          value="public_bridge"
                          checked={networkMode === 'public_bridge'}
                          onChange={() => setNetworkMode('public_bridge')}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Internet Direto — IP Público
                          </p>
                          <p className="text-xs text-gray-500">
                            Sua VM recebe um IPv4 público diretamente na bridge pública.
                          </p>
                          <p className="mt-1 text-xs text-orange-600">
                            Sem proteção de Gateway/Firewall.
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Origem do endereço público (issue #1061): a opção de DHCP só aparece
                      quando `resolve-cluster` confirmou que a WAN do cluster a fornece — e a
                      diferença de cobrança fica à vista ANTES da escolha. */}
                  {networkMode === 'public_bridge' && opcoesDeEndereco.length > 1 && (
                    <div
                      className="space-y-2 rounded-lg border border-gray-200 p-4"
                      data-testid="origem-do-endereco-publico"
                    >
                      <label className="block text-sm font-medium text-gray-700">
                        Origem do endereço público
                      </label>
                      {opcoesDeEndereco.map((modo) => {
                        const d = descricaoDeCobranca(
                          modo,
                          ipv4ProductForSelectedInstance
                            ? formatRecurringPrice(
                                ipv4ProductForSelectedInstance.base_price,
                                ipv4ProductForSelectedInstance.billing_cycle,
                              )
                            : null,
                        )
                        return (
                          <label
                            key={modo}
                            className={`flex cursor-pointer items-start space-x-3 rounded-lg border-2 p-3 transition-colors ${
                              wanMode === modo
                                ? 'border-orange-500 bg-orange-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="wanMode"
                              value={modo}
                              checked={wanMode === modo}
                              onChange={() => {
                                setWanMode(modo)
                                if (modo !== WAN_MODE_POOL) setSelectedPublicIpId(null)
                              }}
                              className="mt-1"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{d.titulo}</p>
                              <p className="text-xs font-semibold text-gray-700">{d.cobranca}</p>
                              <p className="mt-1 text-xs text-gray-500">{d.detalhe}</p>
                            </div>
                          </label>
                        )
                      })}
                    </div>
                  )}

                  {/* Public IP Selection (public_bridge mode, IPv4 fixo do pool) */}
                  {networkMode === 'public_bridge' && wanMode === WAN_MODE_POOL && (
                    <div className="rounded-lg border border-gray-200 p-4">
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Selecione um IPv4
                      </label>
                      {isResolvingInstanceCluster ? (
                        <p className="text-sm text-gray-500">
                          Definindo o cluster de destino da VM...
                        </p>
                      ) : /* Terminar sem destino é FALHA, não espera: sem este ramo, uma
                            resposta 200 sem `cluster_id` deixava a tela parada na mensagem
                            acima para sempre, sem erro, sem spinner e sem nada para
                            clicar (#1084). */
                      hasInstanceClusterResolutionError || !resolvedInstanceClusterId ? (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                          <p className="text-sm text-red-800">
                            Não foi possível definir o cluster de destino da VM.
                          </p>
                          <button
                            type="button"
                            onClick={() => refetchInstanceCluster()}
                            className="mt-1 inline-block text-sm text-blue-600 hover:underline"
                          >
                            Tentar novamente
                          </button>
                        </div>
                      ) : isLoadingAllocatedIps ? (
                        <p className="text-sm text-gray-500">Carregando IPs públicos...</p>
                      ) : hasAllocatedIpsError ? (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                          <p className="text-sm text-red-800">
                            Não foi possível carregar seus IPs públicos.
                          </p>
                          <button
                            type="button"
                            onClick={() => refetchAllocatedIps()}
                            className="mt-1 inline-block text-sm text-blue-600 hover:underline"
                          >
                            Tentar novamente
                          </button>
                        </div>
                      ) : (allocatedIpsData?.items || []).length > 0 ? (
                        <select
                          value={selectedPublicIpId || ''}
                          onChange={(e) => setSelectedPublicIpId(e.target.value || null)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-orange-500"
                        >
                          <option value="">Selecione um IP...</option>
                          {(allocatedIpsData?.items || []).map((ip: PublicIPAllocated) => (
                            <option key={ip.id} value={ip.id}>
                              {ip.ip_address} (disponível)
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                          <p className="text-sm text-yellow-800">
                            Você não possui IPs públicos alocados.
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              if (!ipv4ProductForSelectedInstance) {
                                setAlertModal({
                                  isOpen: true,
                                  title: 'IPv4 indisponível',
                                  message:
                                    'Não há um produto de IPv4 disponível para o catálogo desta VM.',
                                  variant: 'warning',
                                })
                                return
                              }

                              handlePurchaseIPv4(
                                ipv4ProductForSelectedInstance,
                                true,
                                resolvedInstanceClusterId,
                              )
                            }}
                            disabled={isLoadingIPv4Products || !resolvedInstanceClusterId}
                            className="mt-1 inline-block text-sm text-blue-600 hover:underline disabled:cursor-wait disabled:text-slate-400 disabled:no-underline"
                          >
                            {isLoadingIPv4Products ? 'Carregando opções...' : 'Adquira um IPv4 →'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* VPC Selection (VPC mode only) */}
                  {networkMode === 'vpc' && (
                    <div className="rounded-lg border border-gray-200 p-4">
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Rede Privada (VPC)
                      </label>
                      {vpcLoading ? (
                        <p className="text-sm text-gray-500">Carregando redes...</p>
                      ) : clusterVpcs.length > 0 ? (
                        <>
                          <select
                            value={selectedNetworkPoolId || ''}
                            onChange={(e) => setSelectedNetworkPoolId(e.target.value || null)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                          >
                            {clusterVpcs.map((vpc: VPCListItem) => (
                              <option key={vpc.id} value={vpc.id}>
                                {vpc.name} ({vpc.vnet_id}){vpc.is_default ? ' — Padrão' : ''}
                                {vpc.has_gateway === false ? ' — sem gateway' : ''}
                              </option>
                            ))}
                          </select>
                          {selectedNetworkPoolId &&
                            clusterVpcs.find((v) => v.id === selectedNetworkPoolId)?.has_gateway ===
                              false && (
                              <p className="mt-2 text-xs text-orange-600">
                                Esta VPC não tem Cloud Gateway conectado — a VM subirá sem
                                DHCP/internet até você provisionar um gateway.
                              </p>
                            )}
                        </>
                      ) : (
                        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                          <p className="text-sm text-yellow-800">
                            Você não possui uma VPC (rede privada) neste cluster.
                          </p>
                          <button
                            type="button"
                            onClick={() => setShowCreateVPCModal(true)}
                            className="mt-1 inline-block text-sm text-blue-600 hover:underline"
                          >
                            Criar VPC →
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* IP Mode Selection (VPC mode only) */}
                  {networkMode === 'vpc' && (
                    <div className="rounded-lg border border-gray-200 p-4">
                      <label className="mb-3 block text-sm font-medium text-gray-700">
                        Configuração de IP
                      </label>
                      <div className="space-y-2">
                        <label
                          className={`flex cursor-pointer items-start space-x-3 rounded-lg border-2 p-3 transition-colors ${
                            ipMode === 'auto'
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setIpMode('auto')}
                        >
                          <input
                            type="radio"
                            name="ipMode"
                            value="auto"
                            checked={ipMode === 'auto'}
                            onChange={() => setIpMode('auto')}
                            className="mt-1"
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              IP Automático (DHCP)
                            </p>
                            <p className="text-xs text-gray-500">
                              IP atribuído automaticamente pelo gateway via DHCP.
                            </p>
                          </div>
                        </label>
                        <label
                          className={`flex cursor-pointer items-start space-x-3 rounded-lg border-2 p-3 transition-colors ${
                            ipMode === 'static_next'
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setIpMode('static_next')}
                        >
                          <input
                            type="radio"
                            name="ipMode"
                            value="static_next"
                            checked={ipMode === 'static_next'}
                            onChange={() => setIpMode('static_next')}
                            className="mt-1"
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              IP Estático (próximo disponível)
                            </p>
                            <p className="text-xs text-gray-500">
                              Aloca o próximo IP disponível na faixa estática via cloud-init.
                            </p>
                          </div>
                        </label>
                        <label
                          className={`flex cursor-pointer items-start space-x-3 rounded-lg border-2 p-3 transition-colors ${
                            ipMode === 'static_specific'
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setIpMode('static_specific')}
                        >
                          <input
                            type="radio"
                            name="ipMode"
                            value="static_specific"
                            checked={ipMode === 'static_specific'}
                            onChange={() => setIpMode('static_specific')}
                            className="mt-1"
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-900">IP Especifico</p>
                            <p className="text-xs text-gray-500">
                              Escolha um IP da faixa estática para atribuir via cloud-init.
                            </p>
                          </div>
                        </label>
                        {ipMode === 'static_specific' && (
                          <div className="ml-7">
                            <input
                              type="text"
                              value={staticIpAddress}
                              onChange={(e) => setStaticIpAddress(e.target.value)}
                              placeholder="Ex: 10.0.0.25"
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-green-500"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cloud-Init Credentials (Optional) */}
                  <div className="rounded-lg border border-gray-200 p-4">
                    <button
                      type="button"
                      onClick={() => setShowCloudInitConfig(!showCloudInitConfig)}
                      className="flex w-full items-center justify-between text-sm font-medium text-gray-700"
                    >
                      <div className="flex items-center space-x-2">
                        <UserIcon className="h-4 w-4" />
                        <span>Credenciais de Acesso (Opcional)</span>
                      </div>
                      {showCloudInitConfig ? (
                        <ChevronUpIcon className="h-4 w-4" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </button>
                    {showCloudInitConfig && (
                      <div className="mt-3 space-y-3">
                        <p className="text-xs text-gray-500">
                          Se não preencher, será criado com usuário{' '}
                          <span className="font-mono font-medium">{DEFAULT_VM_USER}</span> e senha
                          aleatória.
                        </p>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">
                            Usuário
                          </label>
                          <div className="relative">
                            <UserIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              value={cloudInitUser}
                              onChange={(e) => setCloudInitUser(e.target.value)}
                              placeholder={DEFAULT_VM_USER}
                              maxLength={64}
                              className="w-full rounded-lg border border-gray-300 py-2 pr-3 pl-9 text-sm focus:ring-2 focus:ring-green-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">
                            Senha
                          </label>
                          <div className="relative">
                            <LockIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                              type={showCloudInitPassword ? 'text' : 'password'}
                              value={cloudInitPassword}
                              onChange={(e) => setCloudInitPassword(e.target.value)}
                              placeholder="Deixe vazio para gerar automaticamente"
                              maxLength={128}
                              className="w-full rounded-lg border border-gray-300 py-2 pr-10 pl-9 text-sm focus:ring-2 focus:ring-green-500"
                            />
                            <button
                              type="button"
                              onClick={() => setShowCloudInitPassword(!showCloudInitPassword)}
                              className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              {showCloudInitPassword ? (
                                <EyeOffIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Payment Option Selection */}
                  <div className="rounded-lg border border-gray-200 p-4">
                    <label className="mb-3 block text-sm font-medium text-gray-700">
                      Forma de Pagamento
                    </label>
                    <div className="space-y-3">
                      {/* Credit Card Option */}
                      {enabledPaymentMethods.credit_card && (
                        <div
                          className={`cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                            paymentOption === 'credit_card'
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setPaymentOption('credit_card')}
                        >
                          <div className="flex items-start space-x-3">
                            <input
                              type="radio"
                              name="paymentOption"
                              value="credit_card"
                              checked={paymentOption === 'credit_card'}
                              onChange={() => setPaymentOption('credit_card')}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">Cartão de Crédito</div>
                              <div className="mt-1 text-sm text-gray-600">
                                Pagamento direto via gateway. A cobrança será processada
                                imediatamente.
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Credit Account Option */}
                      {enabledPaymentMethods.credit_account && (
                        <div
                          className={`cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                            paymentOption === 'credit_account'
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setPaymentOption('credit_account')}
                        >
                          <div className="flex items-start space-x-3">
                            <input
                              type="radio"
                              name="paymentOption"
                              value="credit_account"
                              checked={paymentOption === 'credit_account'}
                              onChange={() => setPaymentOption('credit_account')}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">Conta de Crédito</div>
                              <div className="mt-1 text-sm text-gray-600">
                                Usar saldo de crédito pré-pago fornecido pelo seu revendedor.
                              </div>
                              {paymentOption === 'credit_account' && creditBalanceData && (
                                <div
                                  className={`mt-2 text-sm font-medium ${
                                    creditBalanceData.available_credit >=
                                    (calculatedPrice
                                      ? calculatedPrice.total * instanceQuantity
                                      : (selectedProduct?.base_price || 0) * instanceQuantity)
                                      ? 'text-green-700'
                                      : 'text-red-600'
                                  }`}
                                >
                                  Saldo disponível:{' '}
                                  {formatCurrency(
                                    creditBalanceData.available_credit,
                                    creditBalanceData.currency || 'BRL',
                                  )}
                                </div>
                              )}
                              {paymentOption === 'credit_account' &&
                                creditBalanceData &&
                                (() => {
                                  const unitPrice = calculatedPrice
                                    ? calculatedPrice.total
                                    : selectedProduct?.base_price || 0
                                  const totalNeeded = unitPrice * instanceQuantity
                                  if (creditBalanceData.available_credit < totalNeeded) {
                                    return (
                                      <div className="mt-1 text-sm text-red-600">
                                        Saldo insuficiente. Necessário:{' '}
                                        {formatCurrency(
                                          totalNeeded,
                                          selectedProduct?.currency || 'BRL',
                                        )}
                                        . Solicite mais saldo ao seu revendedor.
                                      </div>
                                    )
                                  }
                                  return null
                                })()}
                              {paymentOption === 'credit_account' &&
                                !creditBalanceData?.has_credit_account && (
                                  <div className="mt-1 text-sm text-red-600">
                                    Você não possui conta de crédito. Entre em contato com seu
                                    revendedor.
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* No payment methods available */}
                      {!enabledPaymentMethods.credit_card &&
                        !enabledPaymentMethods.credit_account && (
                          <div className="rounded-r-lg border-l-4 border-yellow-400 bg-yellow-50 p-4">
                            <p className="text-sm text-yellow-700">
                              Nenhum meio de pagamento disponível no momento. Entre em contato com
                              seu revendedor.
                            </p>
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Warning: Association required for credit_account (free customers only) */}
                  {paymentOption === 'credit_account' &&
                    !resellerInfo?.is_captive &&
                    activeAssociations.length === 0 && (
                      <div className="rounded-r-lg border-l-4 border-yellow-400 bg-yellow-50 p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg
                              className="h-5 w-5 text-yellow-400"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">
                              Associação com Reseller Necessária
                            </h3>
                            <div className="mt-2 text-sm text-yellow-700">
                              <p className="mb-2">
                                Para comprar usando conta de crédito, você precisa ter uma{' '}
                                <strong>associação ativa</strong> com o reseller.
                              </p>
                              <p className="mb-2">Você pode:</p>
                              <ul className="list-inside list-disc space-y-1">
                                <li>
                                  Solicitar uma associação na página{' '}
                                  <a href="/resellers" className="font-medium underline">
                                    Resellers
                                  </a>
                                </li>
                                <li>
                                  Ou usar <strong>Cartão de Crédito</strong> para comprar sem
                                  associação
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Payment Method Selector (only for credit_card) */}
                  {paymentOption === 'credit_card' && (
                    <div className="rounded-lg border border-gray-200 p-4">
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Selecione o Cartão
                      </label>
                      {paymentMethods.length > 0 ? (
                        <select
                          value={selectedPaymentMethodId || ''}
                          onChange={(e) => setSelectedPaymentMethodId(e.target.value || null)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-green-500"
                        >
                          <option value="">Selecione um método de pagamento</option>
                          {paymentMethods.map((pm: any) => (
                            <option key={pm.id} value={pm.id}>
                              {pm.card_info?.brand?.toUpperCase() || 'Cartão'} ****{' '}
                              {pm.card_info?.last4 || '****'}
                              {pm.is_default ? ' (Padrão)' : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                          <p className="text-sm text-yellow-800">
                            Nenhum cartão cadastrado. Adicione um cartão na página de Cobrança para
                            usar esta opção.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="xl:col-span-4">
                  <div className="sticky top-0 space-y-4">
                    {/* Real-time Price Calculator */}
                    {selectedProduct.pricing_model === 'per_resource' && calculatedPrice && (
                      <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
                          Estimativa de Preço
                        </h5>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Taxa base</span>
                            <span className="font-medium text-slate-700">
                              {formatCurrency(calculatedPrice.base, selectedProduct.currency)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="flex items-center gap-1 text-slate-500">
                              <CpuIcon className="h-3 w-3" /> {selectedCpu} vCPUs
                            </span>
                            <span className="font-medium text-slate-700">
                              {formatCurrency(calculatedPrice.cpuCost, selectedProduct.currency)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="flex items-center gap-1 text-slate-500">
                              <DatabaseIcon className="h-3 w-3" /> {selectedMemory} GB RAM
                            </span>
                            <span className="font-medium text-slate-700">
                              {formatCurrency(calculatedPrice.memoryCost, selectedProduct.currency)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="flex items-center gap-1 text-slate-500">
                              <HardDriveIcon className="h-3 w-3" /> {selectedStorage} GB SSD
                            </span>
                            <span className="font-medium text-slate-700">
                              {formatCurrency(
                                calculatedPrice.storageCost,
                                selectedProduct.currency,
                              )}
                            </span>
                          </div>
                          <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 font-semibold text-slate-900">
                            <span>Total</span>
                            <span>
                              {formatCurrency(calculatedPrice.total, selectedProduct.currency)}/
                              {getBillingCycleLabel(selectedProduct.billing_cycle)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-5">
                      {(() => {
                        const unitPrice = calculatedPrice
                          ? calculatedPrice.total
                          : selectedProduct.base_price
                        return (
                          <>
                            <div className="text-center">
                              <p className="mb-1 text-xs font-semibold tracking-wider text-emerald-600 uppercase">
                                Custo por VM
                              </p>
                              <p className="text-3xl font-bold tracking-tight text-emerald-700">
                                {formatCurrency(unitPrice, selectedProduct.currency)}
                              </p>
                              <p className="mt-0.5 text-xs text-emerald-600">
                                {getBillingCycleLabel(selectedProduct.billing_cycle)}
                              </p>
                            </div>

                            {/* Total for multiple instances */}
                            {instanceQuantity > 1 && (
                              <div className="mt-3 border-t border-emerald-200 pt-3 text-center">
                                <p className="mb-0.5 text-xs text-emerald-600">
                                  Total ({instanceQuantity} VMs)
                                </p>
                                <p className="text-xl font-bold text-emerald-700">
                                  {formatCurrency(
                                    unitPrice * instanceQuantity,
                                    selectedProduct.currency,
                                  )}
                                </p>
                              </div>
                            )}
                          </>
                        )
                      })()}
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs leading-relaxed text-slate-600">
                        <strong className="text-slate-800">Self-Service:</strong> Sua VM será
                        provisionada automaticamente. A cobrança só será ativada após o
                        provisionamento com sucesso.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer - Fixed */}
            <div className="flex-shrink-0 border-t border-slate-200 bg-slate-50/80 px-6 py-4">
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  onClick={closeModals}
                  className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmitInstance}
                  disabled={
                    createInstanceMutation.isPending ||
                    instanceNames.some((n) => !n.trim()) ||
                    !selectedTemplateId ||
                    motivoParaNaoCriar({
                      networkMode,
                      wanMode,
                      selectedPublicIpId,
                      wanDhcpAvailable,
                    }) !== null ||
                    (paymentOption === 'credit_account' &&
                      (!creditBalanceData?.has_credit_account ||
                        creditBalanceData.available_credit <
                          (calculatedPrice
                            ? calculatedPrice.total
                            : selectedProduct?.base_price || 0) *
                            instanceQuantity))
                  }
                  className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 px-8 py-2.5 text-sm font-semibold text-white shadow-sm shadow-emerald-200 transition-all hover:from-emerald-700 hover:to-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {createInstanceMutation.isPending ? (
                    <>
                      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Criando...
                    </>
                  ) : (
                    <>
                      <PlayIcon className="h-4 w-4" />
                      Criar VM
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* IPv4 Purchase Modal */}
      {showIPv4Modal && selectedIPv4Product && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-orange-100 p-2">
                  <GlobeIcon className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Comprar IPv4 Público</h2>
                  <p className="text-sm text-gray-500">
                    {selectedIPv4Product.name} — R${' '}
                    {selectedIPv4Product.final_price?.toFixed(2) ||
                      selectedIPv4Product.base_price?.toFixed(2)}
                    /mês
                  </p>
                </div>
              </div>
              <button
                onClick={closeIPv4PurchaseModal}
                className="rounded p-1 text-gray-400 hover:text-gray-600"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              {/* Info */}
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                <p className="text-sm text-orange-700">
                  Um endereço IPv4 público será alocado automaticamente para sua organização.
                </p>
              </div>

              {/* Escolha do cluster — aparece só quando o backend devolve 409 de
                  ambiguidade: há IPv4 livre em mais de um cluster do provedor e o
                  endereço só serve para VMs do mesmo cluster (issue #691). */}
              {ipv4ClusterOptions.length > 0 && (
                <div>
                  <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm text-amber-800">
                      Seu provedor tem IPv4 livre em mais de um cluster. O endereço só funciona em
                      VMs do mesmo cluster, então escolha onde pretende usá-lo.
                    </p>
                  </div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Cluster de destino
                  </label>
                  <select
                    value={selectedIpv4ClusterId || ''}
                    onChange={(e) => setSelectedIpv4ClusterId(e.target.value || null)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500"
                  >
                    {ipv4ClusterOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Payment */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Forma de pagamento
                </label>
                <div className="flex gap-3">
                  {enabledPaymentMethods.credit_card && (
                    <label
                      className={`flex flex-1 cursor-pointer items-center gap-2 rounded-lg border p-3 ${paymentOption === 'credit_card' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}
                    >
                      <input
                        type="radio"
                        name="ipv4_payment"
                        value="credit_card"
                        checked={paymentOption === 'credit_card'}
                        onChange={() => setPaymentOption('credit_card')}
                      />
                      <span className="text-sm">Cartão de Crédito</span>
                    </label>
                  )}
                  {enabledPaymentMethods.credit_account && (
                    <label
                      className={`flex flex-1 cursor-pointer items-center gap-2 rounded-lg border p-3 ${paymentOption === 'credit_account' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}
                    >
                      <input
                        type="radio"
                        name="ipv4_payment"
                        value="credit_account"
                        checked={paymentOption === 'credit_account'}
                        onChange={() => setPaymentOption('credit_account')}
                      />
                      <span className="text-sm">Conta de Crédito</span>
                    </label>
                  )}
                </div>
              </div>

              {/* Payment method selector (credit card) */}
              {paymentOption === 'credit_card' && paymentMethods.length > 0 && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Cartão</label>
                  <select
                    value={selectedPaymentMethodId || ''}
                    onChange={(e) => setSelectedPaymentMethodId(e.target.value || null)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Selecione...</option>
                    {paymentMethods
                      .filter((pm: any) => pm.is_active)
                      .map((pm: any) => (
                        <option key={pm.id} value={pm.id}>
                          {pm.card_brand?.toUpperCase()} **** {pm.card_last_four}{' '}
                          {pm.is_default ? '(padrão)' : ''}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {/* Credit balance */}
              {paymentOption === 'credit_account' && creditBalanceData && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <p className="text-sm text-blue-700">
                    Saldo disponível:{' '}
                    <span className="font-semibold">
                      R$ {creditBalanceData?.balance?.toFixed(2) || '0.00'}
                    </span>
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={closeIPv4PurchaseModal}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (!selectedIPv4Product) return
                  purchaseIPv4Mutation.mutate({
                    product_id: selectedIPv4Product.id,
                    payment_option: paymentOption,
                    payment_method_id:
                      paymentOption === 'credit_card' ? selectedPaymentMethodId : undefined,
                    reseller_id: selectedIPv4Product.reseller_id,
                    cluster_id: selectedIpv4ClusterId || undefined,
                  })
                }}
                disabled={purchaseIPv4Mutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm text-white hover:bg-orange-700 disabled:opacity-50"
              >
                {purchaseIPv4Mutation.isPending ? 'Processando...' : 'Confirmar Compra'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => {
          // Pelo TÍTULO, não pelo `variant`: a criação numa VPC sem gateway
          // devolve 'warning' (issue #727) e continua sendo criação bem-sucedida
          // — amarrar a navegação à cor do alerta deixava o cliente parado no
          // Marketplace justamente no caminho novo.
          const wasSuccess = alertModal.title === TITULO_PROVISIONAMENTO
          setAlertModal({ ...alertModal, isOpen: false })
          if (wasSuccess) {
            navigate('/instances')
          }
        }}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
      />

      {/* Create VPC Modal (opens when VM creation fails due to missing VPC) */}
      <CreateVPCModal isOpen={showCreateVPCModal} onClose={() => setShowCreateVPCModal(false)} />
    </div>
  )
}

export default MarketplacePage
